import React from 'react';
import { useFormState } from 'react-final-form';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
  FAQ_ANSWER_MAX_LENGTH,
  FAQ_QUESTION_MAX_LENGTH,
  MAX_FAQ_ITEMS,
} from '../../util/listingPackages';
import { isFaqItemValid } from '../../util/listingCompleteness';
import {
  composeValidators,
  maxLength,
  required,
  requiredRichText,
  richTextToText,
} from '../../util/validators';
import { EditableItemList, FieldTextEditor, FieldTextInput } from '../../components';
import { stateMessages } from '../EditableItemList/EditableItemList';

import css from './EditListingFaqSection.css';

const NAME = 'faq';
const DRAFT_NAME = 'faqDraft';

export const EMPTY_FAQ_ITEM = { question: '', answer: '' };

const FaqSummary = ({ item }) => {
  const excerpt = richTextToText(item.answer).trim();
  return (
    <div className={css.summary}>
      {item.question ? <span className={css.summaryTitle}>{item.question}</span> : null}
      {excerpt ? <span className={css.summaryAnswer}>{excerpt}</span> : null}
    </div>
  );
};

const FaqEditorFields = ({ intl, draftName }) => {
  const requiredMessage = intl.formatMessage({ id: 'EditListingFaqSection.required' });
  const tooLongMessage = max =>
    intl.formatMessage({ id: 'EditListingFaqSection.tooLong' }, { max });
  return (
    <>
      <FieldTextInput
        id={`${draftName}.question`}
        name={`${draftName}.question`}
        type="text"
        label={intl.formatMessage({ id: 'EditListingFaqSection.questionLabel' })}
        validate={composeValidators(
          required(requiredMessage),
          maxLength(tooLongMessage(FAQ_QUESTION_MAX_LENGTH), FAQ_QUESTION_MAX_LENGTH)
        )}
      />
      <FieldTextEditor
        id={`${draftName}.answer`}
        name={`${draftName}.answer`}
        type="textarea"
        label={intl.formatMessage({ id: 'EditListingFaqSection.answerLabel' })}
        isCompact
        maxLength={FAQ_ANSWER_MAX_LENGTH}
        maxLengthMessage={tooLongMessage(FAQ_ANSWER_MAX_LENGTH)}
        validate={requiredRichText(requiredMessage)}
      />
    </>
  );
};

const EditListingFaqSectionComponent = ({ intl }) => {
  const { values } = useFormState({ subscription: { values: true } });
  const faq = values[NAME] || [];
  const draft = values[DRAFT_NAME] || null;

  // Figma copy differs between a new question (Discard · Save question) and a
  // saved one being edited (Cancel · Save changes).
  const message = id => intl.formatMessage({ id: `EditListingFaqSection.${id}` });
  const messages = {
    newTitle: n => intl.formatMessage({ id: 'EditListingFaqSection.newQuestion' }, { n }),
    editTitle: n => intl.formatMessage({ id: 'EditListingFaqSection.editQuestion' }, { n }),
    ...stateMessages({
      draft,
      itemCount: faq.length,
      newItem: { tag: message('draftTag'), save: message('save'), discard: message('discard') },
      savedItem: {
        tag: message('unsavedTag'),
        save: message('saveChanges'),
        discard: message('cancel'),
      },
      add: message('add'),
      addAnother: message('addAnother'),
    }),
    limitNote: intl.formatMessage(
      { id: 'EditListingFaqSection.limitNote' },
      { max: MAX_FAQ_ITEMS }
    ),
  };

  return (
    <section className={css.root}>
      <div className={css.header}>
        <div className={css.titleRow}>
          <h2 className={css.title}>
            <FormattedMessage id="EditListingFaqSection.title" />
          </h2>
          <span className={css.count}>
            <FormattedMessage
              id="EditListingFaqSection.count"
              values={{ count: faq.length, max: MAX_FAQ_ITEMS }}
            />
          </span>
        </div>
        <p className={css.description}>
          <FormattedMessage
            id="EditListingFaqSection.description"
            values={{ max: MAX_FAQ_ITEMS }}
          />
        </p>
      </div>
      <EditableItemList
        name={NAME}
        draftName={DRAFT_NAME}
        maxItems={MAX_FAQ_ITEMS}
        emptyItem={EMPTY_FAQ_ITEM}
        isItemValid={isFaqItemValid}
        renderSummary={item => <FaqSummary item={item} />}
        renderEditorFields={({ draftName }) => (
          <FaqEditorFields intl={intl} draftName={draftName} />
        )}
        messages={messages}
      />
    </section>
  );
};

EditListingFaqSectionComponent.propTypes = {
  intl: intlShape.isRequired,
};

const EditListingFaqSection = injectIntl(EditListingFaqSectionComponent);

export default EditListingFaqSection;
